import { Images } from '@/assets/images';
import { DocTypeEnum } from '@/constant';
import { cls, dayjs, formatDate } from '@/utils';

import { FormConfig, PageConfig } from '../define-document-write.type';

const initializeValue = {
	districtName: '',
	personalInfoAgree: '1',
	personalInfoDisagree: '1',
	uniqueIdInfoAgree: '1',
	uniqueIdInfoDisagree: '1',

	acceptProRegistDate: '',
	employerName: '',
	employerSignature: '',
	foreignWorkerName: '',
	foreignWorkerSignature: '',
	addresseeAdminName: '',
	addresseeOfficialSeal: '',
	countryDistrict: '',
};

const PERSONAL_INF_AGREE_OPTIONS = ['1', '0'];
const PERSONAL_INF_DISAGREE_OPTIONS = ['1', '0'];
const UNIQUE_ID_INF_AGREE_OPTIONS = ['1', '0'];
const UNIQUE_ID_INF_DISAGREE_OPTIONS = ['1', '0'];

type IValues = typeof initializeValue;

/**
 * Pages used when the form is opened from the APPLICANT (근로자) side.
 */
const applicantPages: PageConfig<IValues>[] = [
	{
		background: {
			src: Images.formARFApplicant,
			className: 'template',
			dataSource: Images.formARFApplicant,
		},
		textAreas: [
			(values, index) => ({
				name: 'districtName',
				maxHeight: 43,
				className: 'text-[13px] text-left',
				containerClassName: cls(
					'absolute top-[174px] left-[69px] !h-[43px] w-[70px] items-end',
					values?.districtName && 'bg-white'
				),
				maxLength: 20,
			}),
		],
		checkboxes: [
			{
				name: 'personalInfoAgree',
				classNames: [
					'absolute top-[858px] left-[70px] w-6 h-6',
					'absolute top-[858px] left-[200px] w-6 h-6',
				],
				iconType: 'checkbox',
				options: PERSONAL_INF_AGREE_OPTIONS,
			},

			{
				name: 'personalInfoDisagree',
				classNames: [
					'absolute top-[947px] left-[70px] w-6 h-6',
					'absolute top-[947px] left-[200px] w-6 h-6',
				],
				iconType: 'checkbox',
				options: PERSONAL_INF_DISAGREE_OPTIONS,
			},
		],
		highlightBoxes: [
			{
				name: 'districtName',
				className: 'top-[196px] left-[69px] !h-[21px] w-[70px]',
			},
			(values, index) => ({
				name: 'personalInfoAgree',
				className: 'top-[862px] left-[70px] !h-[19px] w-[305px]',
				hasError: values?.personalInfoAgree !== PERSONAL_INF_AGREE_OPTIONS[0],
			}),
			(values, index) => ({
				name: 'personalInfoDisagree',
				className: 'top-[948px] left-[70px] !h-[19px] w-[305px]',
				hasError:
					values?.personalInfoDisagree !== PERSONAL_INF_DISAGREE_OPTIONS[0],
			}),
		],
	},
	{
		background: {
			src: Images.formARFApplicant2,
			className: 'template',
			dataSource: Images.formARFApplicant2,
		},
		checkboxes: [
			{
				name: 'uniqueIdInfoAgree',
				classNames: [
					'absolute top-[719px] left-[70px] w-6 h-6',
					'absolute top-[719px] left-[200px] w-6 h-6',
				],
				iconType: 'checkbox',
				options: UNIQUE_ID_INF_AGREE_OPTIONS,
			},

			{
				name: 'uniqueIdInfoDisagree',
				classNames: [
					'absolute top-[820px] left-[70px] w-6 h-6',
					'absolute top-[820px] left-[200px] w-6 h-6',
				],
				iconType: 'checkbox',
				options: UNIQUE_ID_INF_DISAGREE_OPTIONS,
			},
		],
		datePickers: [
			{
				name: 'acceptProRegistDate',
				segmentedInputs: true,
				format: 'YYYY           MM            DD',
				containerClassName: 'absolute top-[857px] left-[505px] ',
				style: { fontSize: '14px' },
				boxStyle: { marginTop: '-7px' },
			},
		],
		textAreas: [
			(values, index) => ({
				name: 'employerName',
				maxHeight: 24,
				className: 'text-base text-left',
				containerClassName: cls(
					'absolute top-[920px] left-[360px] w-[185px] h-[36px]',
					values?.employerName && 'bg-white'
				),
				maxLength: 14,
			}),
			(values, index) => ({
				name: 'foreignWorkerName',
				maxHeight: 24,
				className: 'text-base text-left',
				containerClassName: cls(
					'absolute top-[918px] left-[360px] w-[185px] h-[36px]',
					values?.foreignWorkerName && 'bg-white'
				),
				maxLength: 14,
			}),
			(values, index) => ({
				name: 'countryDistrict',
				maxHeight: 40,
				className: 'text-[16px] text-left leading-none',
				containerClassName: cls('absolute top-[965px] left-[180px] w-[122px]'),
				maxLength: 35,
			}),
		],
		signatures: [
			{
				name: 'employerSignature',
				className: 'absolute top-[907px] left-[540px] w-[125px] h-[48px]',
			},
		],
		highlightBoxes: [
			(values, index) => ({
				name: 'uniqueIdInfoAgree',
				className: 'top-[722px] left-[70px] !h-[19px] w-[305px]',
				hasError: values?.uniqueIdInfoAgree !== UNIQUE_ID_INF_AGREE_OPTIONS[0],
			}),
			(values, index) => ({
				name: 'uniqueIdInfoDisagree',
				className: 'top-[822px] left-[70px] !h-[19px] w-[305px]',
				hasError:
					values?.uniqueIdInfoDisagree !== UNIQUE_ID_INF_DISAGREE_OPTIONS[0],
			}),
			{
				name: 'acceptProRegistDate',
				className: 'top-[850px] left-[490px] !h-[21px] w-[200px]',
			},
			{
				name: 'employerName',
				className: 'top-[920px] left-[117px] !h-[21px] w-[233px]',
			},
			{
				name: 'employerSignature',
				className: 'top-[920px] left-[545px] !h-[21px] w-[110px]',
			},
			{
				name: 'countryDistrict',
				className: 'top-[973px] left-[180px] !h-[21px] w-[122px]',
			},
		],
	},
];

export const MANDATORY_INSURANCE_CONSENT_CONFIG: FormConfig<
	IValues,
	DocTypeEnum.ARF_11
> = {
	initializeValue,
	title: '개인정보 수집 및 이용 제공 동의(의무보험용_계절근로자용)',
	mapPropToValues: ({
		refTableName,
		detailData,
		franchiseData,
		applicantData,
		unitData,
	}) => {
		const { adminInfo, unitInfo } = detailData || {};
		return {
			districtName: unitInfo?.countyDistrict,
			acceptProRegistDate: formatDate(dayjs()),
			employerName: franchiseData?.employerName || '',
			foreignWorkerName: applicantData?.fullName || '',
			addresseeAdminName: unitInfo?.countyDistrict,
			addresseeOfficialSeal:
				unitData?.companySealFileId || adminInfo?.unitCompanySealFilePath || '',
			countryDistrict: `${unitInfo?.cityProvince} ${unitInfo?.countyDistrict ? `(${unitInfo?.countyDistrict})` : ''}`,
		};
	},
	pages: ({ refTableName }) => applicantPages,
};
